import { useState, useEffect, useMemo } from 'react';
import { getAddresses } from 'api';
import type { AddressType, UbigeoIndexMatch } from 'interfaces';

interface AddressIndexRow {
  department: number;
  province: number;
  district: number;
  index?: string | number;
  ubigeo?: string | number;
}

const getIndexPart = (value?: string | number): string | null => {
  const digits = String(value ?? '').replace(/\D/g, '');
  if (!digits) {
    return null;
  }
  return digits.slice(-2).padStart(2, '0');
};

const getDirectIndex = (row: AddressIndexRow): string | null => {
  const digits = String(row.index ?? row.ubigeo ?? '').replace(/\D/g, '');
  if (digits.length !== 6) {
    return null;
  }
  return digits;
};

const getById = <T>(map: Map<number, T>, id: number | string): T | undefined => {
  return map.get(id as number) ?? map.get(Number(id));
};

const getComposedIndex = (
  row: AddressIndexRow,
  departmentMap: Map<number, AddressType>,
  provinceMap: Map<number, AddressType>,
  districtMap: Map<number, AddressType>,
): string | null => {
  const department = getById(departmentMap, row.department);
  const province = getById(provinceMap, row.province);
  const district = getById(districtMap, row.district);

  if (!department || !province || !district) {
    return null;
  }

  const departmentPart = getIndexPart(department.index);
  const provincePart = getIndexPart(province.index);
  const districtPart = getIndexPart(district.index);

  if (!departmentPart || !provincePart || !districtPart) {
    return null;
  }

  return `${departmentPart}${provincePart}${districtPart}`;
};

export const useFetchDepartments = () => {
  const [allAddresses, setAllAddresses] = useState<any>();
  const [departments, setDepartments] = useState<AddressType[]>([]);
  const [provincesCodeMap, setProvinces] = useState<any>();

  useEffect(() => {
    let stillMounted = true;

    const getData = async () => {
      try {
        const response = await getAddresses();
        const data = response.data;

        if (!data?.departments || !data?.provinces || !data?.districts || !data?.indexes) {
          setAllAddresses(undefined);
          setDepartments([]);
          return;
        }

        setAllAddresses(data);
        setDepartments(data.departments);
      } catch {
        setAllAddresses(undefined);
        setDepartments([]);
      }
    };

    if (stillMounted) {
      getData();
    }
    return () => {
      stillMounted = false;
    };
  }, []);

  const addresses = useMemo(() => {
    if (!allAddresses?.departments || !allAddresses?.provinces || !allAddresses?.districts || !allAddresses?.indexes) {
      return undefined;
    }

    const departmentMap = new Map<number, AddressType>();
    const provinceMap = new Map<number, AddressType>();
    const districtMap = new Map<number, AddressType>();
    const provinceMapCode = new Map<string, AddressType>();

    allAddresses.departments.forEach((dep: AddressType) => departmentMap.set(dep.id, dep));
    allAddresses.provinces.forEach((province: AddressType) => provinceMap.set(province.id, province));
    allAddresses.districts.forEach((district: AddressType) => districtMap.set(district.id, district));
    allAddresses.provinces.forEach((province: AddressType) =>
      provinceMapCode.set(province.code.toLowerCase().replace(' ', '_'), province),
    );
    setProvinces(provinceMapCode);

    const departmentToProvinces = new Map<number, Set<number>>();
    const provinceToDistricts = new Map<number, Set<number>>();
    const ubigeoIndexes = new Map<string, UbigeoIndexMatch>();

    const addUbigeoIndex = (code: string | null, row: AddressIndexRow) => {
      if (!code || ubigeoIndexes.has(code)) {
        return;
      }

      const department = getById(departmentMap, row.department);
      const province = getById(provinceMap, row.province);
      const district = getById(districtMap, row.district);

      if (!department || !province || !district) {
        return;
      }

      ubigeoIndexes.set(code, { department, province, district });
    };

    for (const row of allAddresses.indexes as AddressIndexRow[]) {
      if (!departmentToProvinces.has(row.department)) {
        departmentToProvinces.set(row.department, new Set());
      }
      if (!provinceToDistricts.has(row.province)) {
        provinceToDistricts.set(row.province, new Set());
      }

      departmentToProvinces.get(row.department)?.add(row.province);
      provinceToDistricts.get(row.province)?.add(row.district);

      addUbigeoIndex(getDirectIndex(row), row);
      addUbigeoIndex(getComposedIndex(row, departmentMap, provinceMap, districtMap), row);
    }

    return {
      departmentMap,
      provinceMap,
      districtMap,
      departmentToProvinces,
      provinceToDistricts,
      ubigeoIndexes,
    };
  }, [allAddresses]);

  return {
    departments,
    addresses,
    provincesCodeMap,
    ubigeoIndexes: addresses?.ubigeoIndexes,
  };
};
